import { all, call, delay, put, select, takeLatest } from 'redux-saga/effects';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import utc from 'dayjs/plugin/utc';
import isBetween from 'dayjs/plugin/isBetween';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import arraySupport from 'dayjs/plugin/arraySupport';

import { RT_API_USAGE } from 'src/store/ActionTypes';
import * as api from 'src/store/api';
import * as actions from 'src/store/actions/rt_api_usage';
import CONSTANTS from 'src/store/constants';

dayjs.extend(utc);
dayjs.extend(customParseFormat);
dayjs.extend(isBetween);
dayjs.extend(isSameOrAfter);
dayjs.extend(arraySupport);

const selectFilter = (state) => state.rtApiUsage.filter;
const selectEntries = (state) => state.rtApiUsage.usageEntries;
const selectMasks = (state) => state.rtApiUsage.usageMasks;
const selectClientBillingDay = (state) => state.app?.client?.billingDay;
const isMatchInWindow = (isMatch, inWindow) => isMatch && inWindow;
const today = dayjs.utc();

function roundn(num, target) {
  return Math.round(num / target) * target;
}

function createToDateMetricFilters(metric, cutOffDate, cycles) {
  const cutOffDateEnd = dayjs.utc([dayjs.utc().year(), dayjs.utc().month(), +cutOffDate]);
  const timestamps = [
    {
      and: [
        { name: 'end_timestamp', op: 'gt', val: today.subtract(1, 'd').startOf('day').unix() },
        { name: 'end_timestamp', op: 'lt', val: today.startOf('day').unix() },
      ],
    },
  ];
  for (let i = 0; i < cycles - 1; i += 1) {
    timestamps.push({
      and: [
        { name: 'end_timestamp', op: 'gt', val: cutOffDateEnd.subtract(i, 'M').subtract(1, 'd').startOf('day').unix() },
        { name: 'end_timestamp', op: 'lt', val: cutOffDateEnd.subtract(i, 'M').startOf('day').unix() },
      ],
    });
  }
  const filters = [{ name: 'metric_name', op: 'eq', val: metric }, { or: timestamps }];
  return filters;
}

function createMonthlyMetricFilters(cycles) {
  const timestamps = [];
  for (let i = 0; i < cycles; i += 1) {
    timestamps.push({
      and: [
        { name: 'end_timestamp', op: 'gt', val: today.subtract(1, 'd').subtract(i, 'M').startOf('day').unix() },
        { name: 'end_timestamp', op: 'lt', val: today.subtract(i, 'M').startOf('day').unix() },
      ],
    });
  }
  const filters = [{ name: 'metric_name', op: 'eq', val: 'MONTHLY' }, { or: timestamps }];
  return filters;
}

const rttQueryBuilder = (filter, billingDay, configList = []) => {
  const rttQuery = {
    filters: [],
    order_by: [{ field: 'end_timestamp', direction: 'desc' }],
  };
  const queries = {
    primary: { filters: [] },
    toDate: {
      filters: [
        { name: 'metric_name', op: 'in', val: ['YEAR_TO_DATE', 'MONTH_TO_DATE', 'WEEK_TO_DATE'] },
        { name: 'end_timestamp', op: 'gte', val: today.subtract(1, 'd').startOf('day').unix() },
      ],
    },
  };
  if (configList.includes(null) || !configList?.length) rttQuery.filters.push({ name: 'config_id', op: 'is_null' });
  else rttQuery.filters.push({ name: 'config_id', op: 'in', val: configList });

  switch (filter) {
    case CONSTANTS.REAL_TIME_API.USAGE.FILTER.LAST_12_BILLING_CYCLES:
      queries.primary = {
        filters: [
          { name: 'metric_name', op: 'eq', val: '12_MONTH_BILLING' },
          { name: 'end_timestamp', op: 'gte', val: today.subtract(1, 'M').startOf('day').unix() },
        ],
      };
      queries.monthly = { filters: createToDateMetricFilters('LAST_BILLING_TO_DATE', billingDay, 12) };
      break;
    case CONSTANTS.REAL_TIME_API.USAGE.FILTER.LAST_6_BILLING_CYCLES:
      queries.primary = {
        filters: [
          { name: 'metric_name', op: 'eq', val: '6_MONTH_BILLING' },
          { name: 'end_timestamp', op: 'gte', val: today.subtract(1, 'M').startOf('day').unix() },
        ],
      };
      queries.monthly = { filters: createToDateMetricFilters('LAST_BILLING_TO_DATE', billingDay, 6) };
      break;
    case CONSTANTS.REAL_TIME_API.USAGE.FILTER.LAST_3_BILLING_CYCLES:
      queries.primary = {
        filters: [
          { name: 'metric_name', op: 'eq', val: '3_MONTH_BILLING' },
          { name: 'end_timestamp', op: 'gte', val: today.subtract(1, 'M').startOf('day').unix() },
        ],
      };
      queries.monthly = { filters: createToDateMetricFilters('LAST_BILLING_TO_DATE', billingDay, 3) };
      break;
    case CONSTANTS.REAL_TIME_API.USAGE.FILTER.LAST_12_MONTHS:
      queries.primary = {
        filters: [
          { name: 'metric_name', op: 'eq', val: 'YEARLY' },
          { name: 'end_timestamp', op: 'gte', val: today.subtract(1, 'd').startOf('day').unix() },
        ],
      };
      queries.monthly = { filters: createMonthlyMetricFilters(12) };
      break;
    case CONSTANTS.REAL_TIME_API.USAGE.FILTER.LAST_30_DAYS:
      queries.primary = {
        filters: [
          { name: 'metric_name', op: 'eq', val: 'MONTHLY' },
          { name: 'end_timestamp', op: 'gte', val: today.subtract(1, 'd').startOf('day').unix() },
        ],
      };
      queries.daily = {
        filters: [
          { name: 'metric_name', op: 'eq', val: 'DAILY' },
          { name: 'end_timestamp', op: 'gte', val: today.subtract(1, 'M').startOf('day').unix() },
        ],
      };
      break;
    case CONSTANTS.REAL_TIME_API.USAGE.FILTER.LAST_7_DAYS:
      queries.primary = {
        filters: [
          { name: 'metric_name', op: 'eq', val: 'WEEKLY' },
          { name: 'end_timestamp', op: 'gte', val: today.subtract(7, 'd').startOf('day').unix() },
        ],
      };
      queries.daily = {
        filters: [
          { name: 'metric_name', op: 'eq', val: 'DAILY' },
          { name: 'end_timestamp', op: 'gte', val: today.subtract(7, 'd').startOf('day').unix() },
        ],
      };
      break;
    default:
      break;
  }
  for (const query in queries) {
    if (Object.prototype.hasOwnProperty.call(queries, query)) {
      queries[query].filters = [...queries[query].filters, ...rttQuery.filters];
      queries[query].order_by = rttQuery.order_by;
    }
  }
  return queries;
};

/**
 *
 * @return {import('src/store/typings.js').RtCs}
 */
const selectConfig = (state) => state.rtApiUsage.config;

/**
 * updateFilter - flow control for updating the filter view
 * @param {{type: string, payload: string }} action
 * @returns {Generator<*, void, *>}
 */
function* updateFilter(action) {
  try {
    // NOTE: Check if the config has been set from the list page, and matches the requested config
    const { payload: filter } = action;
    const billingDay = yield select(selectClientBillingDay);
    const config = yield select(selectConfig);
    let rttQuery;
    if (config) rttQuery = rttQueryBuilder(filter, billingDay, [config?.id]);
    else rttQuery = rttQueryBuilder(filter, billingDay);
    yield put({
      type: RT_API_USAGE.FETCH_RTT_ENTRIES,
      payload: rttQuery,
    });
  } catch (err) {
    yield put({
      type: RT_API_USAGE.FETCH_RTT_ENTRIES_FAILED,
      payload: err.message,
    });
  } finally {
    // pass
  }
}

/**
 * fetchConfig - flow control for fetching a realtime config
 * @param {{type: string, payload: string }} action
 * @returns {Generator<*, void, *>}
 */
function* fetchConfig(action) {
  try {
    // NOTE: Check if the config has been set from the list page, and matches the requested config
    const { payload: configId } = action;
    const billingDay = yield select(selectClientBillingDay);
    const filter = yield select(selectFilter);
    /** @type {import('src/store/typings.js').RtCs} config */
    let config = yield select(selectConfig);
    if ([null, undefined].includes(config) || configId !== config?.id) {
      config = yield call(api.fetchRtConfig, configId);
    }
    if (config.score_type === 'static')
      config.config_metadata.model = {
        name: 'Test Environment',
        typeDetail: 'TEST',
        refresh: {
          platform: CONSTANTS.PLATFORM.REFRESH_VERSIONS.UPGRADED,
        },
        modelData: {
          metadataPayload: {
            refresh: {
              platform: CONSTANTS.PLATFORM.REFRESH_VERSIONS.UPGRADED,
            },
          },
        },
      };
    else if (!config.config_metadata.model_id) {
      config.config_metadata.model = {
        id: -1 * Math.random(),
        name: config.config_metadata.name || 'CUSTOM MODEL',
        typeDetail: config.config_metadata.type_detail || 'CUSTOM TYPE',
        dataModelType: config.config_metadata.data_model_type || 'CLASSIFIER',
      };
    } else config.config_metadata.model = yield call(api.fetchModel, config.config_metadata.model_id);
    yield put({
      type: RT_API_USAGE.FETCH_CONFIG_SUCCEEDED,
      payload: config,
    });
    const usageQuery = {
      filters: [
        { val: configId, op: 'eq', name: 'name' },
        { name: 'period', op: 'gte', val: today.subtract(366, 'd').format('YYYY-MM-DD') },
      ],
      order_by: [{ field: 'period', direction: 'asc' }],
    };
    const rttQuery = rttQueryBuilder(filter, billingDay, [config?.id]);
    yield put({
      type: RT_API_USAGE.FETCH_USAGE_ENTRIES,
      payload: usageQuery,
    });
    yield put({
      type: RT_API_USAGE.FETCH_RTT_ENTRIES,
      payload: rttQuery,
    });
  } catch (err) {
    yield put({
      type: RT_API_USAGE.FETCH_CONFIG_FAILURE,
      payload: err.message,
    });
  } finally {
    yield delay(500);
    yield put(actions.unset('fetchConfigError'));
  }
}

/**
 * fetchConfigList - flow control for fetching a list of configs
 * @param {{type: string, payload: string }} action
 * @returns {Generator<*, void, *>}
 */
function* fetchConfigList(action) {
  try {
    const { payload: query } = action;
    const billingDay = yield select(selectClientBillingDay);
    const filter = yield select(selectFilter);
    const configList = yield call(api.fetchRtConfigs, query);
    const staticConfigList = yield call(api.fetchRtConfigs, {
      filters: [
        { name: 'recent_status', op: 'eq', val: 'CONFIG_GENERATION_SUCCESS' },
        { name: 'score_type', op: 'eq', val: 'static' },
      ],
      order_by: [{ field: 'created_at', direction: 'asc' }],
    });
    const staticConfigIdList = staticConfigList.map((cs) => cs.id);
    const modelList = configList.map((cs) => {
      if (cs.score_type === 'static')
        return {
          name: 'Test Environment',
          typeDetail: 'TEST',
          refresh: {
            platform: CONSTANTS.PLATFORM.REFRESH_VERSIONS.UPGRADED,
          },
          modelData: {
            metadataPayload: {
              refresh: {
                platform: CONSTANTS.PLATFORM.REFRESH_VERSIONS.UPGRADED,
              },
            },
          },
        };
      if (!cs.config_metadata.model_id) {
        return {
          id: -1 * Math.random(),
          name: cs.config_metadata.name || 'CUSTOM MODEL',
          typeDetail: cs.config_metadata.type_detail || 'CUSTOM TYPE',
          dataModelType: cs.config_metadata.data_model_type || 'CLASSIFIER',
        };
      }
      const options = {
        headers: {
          'X-Fields': 'name,dataModelType,predictionType,typeDetail',
        },
      };
      return call(api.fetchModel, cs.config_metadata.model_id, options);
    });
    const results = yield all(modelList);
    results.forEach((model, idx) => {
      configList[idx].config_metadata.model = model;
    });
    /** @param {import('src/store/typings.js').RtCs} cs */
    yield put({
      type: configList?.length
        ? RT_API_USAGE.FETCH_CONFIG_LIST_SUCCEEDED
        : RT_API_USAGE.FETCH_CONFIG_LIST_SUCCEEDED_EMPTY,
      payload: configList,
    });
    const usageQuery = {
      filters: [
        {
          // NOTE: should include only scores and non-static configIds
          val: ['enrich', 'matches', 'segment_modeling', 'segment_prediction', 'tal_score', 'submit'].concat(
            staticConfigIdList
          ),
          op: 'not_in',
          name: 'name',
        },
        { name: 'period', op: 'gte', val: today.subtract(366, 'd').format('YYYY-MM-DD') },
      ],
      order_by: [{ field: 'period', direction: 'asc' }],
    };
    const rttQuery = rttQueryBuilder(filter, billingDay);
    yield put({
      type: RT_API_USAGE.FETCH_USAGE_ENTRIES,
      payload: usageQuery,
    });
    yield put({
      type: RT_API_USAGE.FETCH_RTT_ENTRIES,
      payload: rttQuery,
    });
  } catch (e) {
    yield put({
      type: RT_API_USAGE.FETCH_CONFIG_LIST_FAILURE,
      payload: e.message,
    });
  } finally {
    yield delay(500);
    yield put(actions.unset('fetchConfigListError'));
  }
}

/**
 * fetchRttEntries - flow control for fetching a list of return time usageEntries
 * @param {{type: string, payload: object }} action
 * @returns {Generator<*, void, *>}
 */
function* fetchRttEntries(action) {
  try {
    const { payload: query } = action;
    const binRounding = CONSTANTS.REAL_TIME_API.USAGE.BIN_ROUNDING;
    const filter = yield select(selectFilter);
    const filterMetric = CONSTANTS.REAL_TIME_API.USAGE.FILTER_METRIC_MAP[filter];
    const calls = [call(api.fetchRttEntries, query.primary), call(api.fetchRttEntries, query.toDate)];
    if (query.monthly) {
      calls.push(call(api.fetchRttEntries, query.monthly));
    }
    if (query.daily) {
      calls.push(call(api.fetchRttEntries, query.daily));
    }
    const [rttEntries, rttToDateEntries, rttSecondaryEntries] = yield all(calls);
    const rttEntriesFiltered = rttEntries.filter((v) => v.metric_name === filterMetric);
    const rttEntryLatest = rttEntriesFiltered.length
      ? rttEntriesFiltered.reduce((prev, current) => (prev.end_timestamp > current.end_timestamp ? prev : current))
      : undefined;
    const rttEntry = rttEntryLatest ?? {};
    const rttStats = {
      mean: ((rttEntry?.metric_metadata?.duration?.location?.duration?.mean ?? 0) * 1000).toFixed(0),
      p50: ((rttEntry?.metric_metadata?.duration?.location?.duration?.['50%'] ?? 0) * 1000).toFixed(0),
      p75: ((rttEntry?.metric_metadata?.duration?.location?.duration?.['75%'] ?? 0) * 1000).toFixed(0),
      p90: ((rttEntry?.metric_metadata?.duration?.location?.duration?.['90%'] ?? 0) * 1000).toFixed(0),
    };

    const rttHist =
      rttEntry?.metric_metadata?.duration?.hist
        ?.map?.(({ left, right, value, count }) => ({
          left: left * 1000,
          right: right * 1000,
          value: value * 1000,
          count,
        }))
        ?.reduce?.((acc, cur) => {
          const roundedVal = roundn(cur.value, binRounding);
          let idx = acc.findIndex(({ value }) => value === roundedVal);
          if (roundedVal > 10000) idx = acc.length - 1;
          if (idx !== -1) {
            // already seen, add counts
            acc[idx].count += cur.count;
          } else if (roundedVal <= 10000) acc.push({ count: cur.count, value: roundedVal });
          return acc;
        }, []) ?? [];
    yield put({
      type: RT_API_USAGE.FETCH_RTT_ENTRIES_SUCCEEDED,
      payload: { rttHist, rttStats, rttEntries, rttSecondaryEntries, rttToDateEntries },
    });
  } catch (e) {
    yield put({
      type: RT_API_USAGE.FETCH_RTT_ENTRIES_FAILED,
      payload: e.message,
    });
  } finally {
    yield delay(500);
    yield put(actions.unset('fetchRttEntriesError'));
  }
}

/**
 * fetchUsageEntries - flow control for fetching a list of usage tracking usageEntries
 * @param {{type: string, payload: string }} action
 * @returns {Generator<*, void, *>}
 */
function* fetchUsageEntries(action) {
  try {
    const { payload: query } = action;
    const billingDay = yield select(selectClientBillingDay);
    const entries = yield call(api.fetchTrackingEntries, query);
    const masks = {
      ytdMatched: new Array(entries.length).fill(false),
      mtdMatched: new Array(entries.length).fill(false),
      btdMatched: new Array(entries.length).fill(false),
      tdyMatched: new Array(entries.length).fill(false),
      '7dMatched': new Array(entries.length).fill(false),
      '30dMatched': new Array(entries.length).fill(false),
      '1yMatched': new Array(entries.length).fill(false),
      '3cMatched': new Array(entries.length).fill(false),
      '6cMatched': new Array(entries.length).fill(false),
      '12cMatched': new Array(entries.length).fill(false),
      ytdUnmatched: new Array(entries.length).fill(false),
      mtdUnmatched: new Array(entries.length).fill(false),
      btdUnmatched: new Array(entries.length).fill(false),
      tdyUnmatched: new Array(entries.length).fill(false),
      '7dUnmatched': new Array(entries.length).fill(false),
      '30dUnmatched': new Array(entries.length).fill(false),
      '1yUnmatched': new Array(entries.length).fill(false),
      '3cUnmatched': new Array(entries.length).fill(false),
      '6cUnmatched': new Array(entries.length).fill(false),
      '12cUnmatched': new Array(entries.length).fill(false),
      ytdErrors: new Array(entries.length).fill(false),
      mtdErrors: new Array(entries.length).fill(false),
      btdErrors: new Array(entries.length).fill(false),
      tdyErrors: new Array(entries.length).fill(false),
      '7dErrors': new Array(entries.length).fill(false),
      '30dErrors': new Array(entries.length).fill(false),
      '1yErrors': new Array(entries.length).fill(false),
      '3cErrors': new Array(entries.length).fill(false),
      '6cErrors': new Array(entries.length).fill(false),
      '12cErrors': new Array(entries.length).fill(false),
      ytdTotal: new Array(entries.length).fill(false),
      mtdTotal: new Array(entries.length).fill(false),
      btdTotal: new Array(entries.length).fill(false),
      tdyTotal: new Array(entries.length).fill(false),
      '7dTotal': new Array(entries.length).fill(false),
      '30dTotal': new Array(entries.length).fill(false),
      '1yTotal': new Array(entries.length).fill(false),
      '3cTotal': new Array(entries.length).fill(false),
      '6cTotal': new Array(entries.length).fill(false),
      '12cTotal': new Array(entries.length).fill(false),
    };
    const ytdCutoff = dayjs.utc([dayjs.utc().year(), 0]);
    const mtdCutoff = dayjs.utc([dayjs.utc().year(), dayjs.utc().month()]);
    const weekCutoff = dayjs.utc().subtract(7, 'day');
    const monthCutoff = dayjs.utc().subtract(30, 'day');
    const yearCutoff = dayjs.utc().subtract(1, 'year');
    // Generate the masks for entry filters
    // Walk the usageEntries once, setting boolean masks as we go for each category
    entries.forEach((entry, idx) => {
      let billingWindowStart = dayjs.utc([dayjs.utc().year(), dayjs.utc().month(), billingDay]);
      const isNewBillingPeriod = dayjs.utc().isSameOrAfter(billingWindowStart);
      billingWindowStart = isNewBillingPeriod ? billingWindowStart : billingWindowStart.subtract(1, 'month');
      const billingWindowEnd = billingWindowStart.add(1, 'month');
      const billingWindowStart3c = billingWindowEnd.subtract(3, 'month');
      const billingWindowStart6c = billingWindowEnd.subtract(6, 'month');
      const billingWindowStart12c = billingWindowEnd.subtract(12, 'month');
      const entryPeriod = dayjs.utc(entry.period, 'YYYY-MM-DD');
      const isMatch = entry.meta === 'matched';
      const notMatch = entry.meta === 'NULL';
      const isError = entry.meta === 'error';
      const entryToday = entryPeriod.isSame(today, 'date');
      const entryThisYear = entryPeriod.isSameOrAfter(ytdCutoff);
      const entryThisMonth = entryPeriod.isSameOrAfter(mtdCutoff);
      const entryThisBillingCycle = entryPeriod.isSameOrAfter(billingWindowStart);
      const entryInWeekWindow = entryPeriod.isSameOrAfter(weekCutoff);
      const entryInMonthWindow = entryPeriod.isSameOrAfter(monthCutoff);
      const entryInYearWindow = entryPeriod.isSameOrAfter(yearCutoff);
      const entryInBillingWindow3c = entryPeriod.isBetween(billingWindowStart3c, billingWindowEnd, 'day', '[)');
      const entryInBillingWindow6c = entryPeriod.isBetween(billingWindowStart6c, billingWindowEnd, 'day', '[)');
      const entryInBillingWindow12c = entryPeriod.isBetween(billingWindowStart12c, billingWindowEnd, 'day', '[)');

      // Match
      masks.ytdMatched[idx] = isMatchInWindow(isMatch, entryThisYear);
      masks.mtdMatched[idx] = isMatchInWindow(isMatch, entryThisMonth);
      masks.btdMatched[idx] = isMatchInWindow(isMatch, entryThisBillingCycle);
      masks.tdyMatched[idx] = isMatchInWindow(isMatch, entryToday);
      masks['7dMatched'][idx] = isMatchInWindow(isMatch, entryInWeekWindow);
      masks['30dMatched'][idx] = isMatchInWindow(isMatch, entryInMonthWindow);
      masks['1yMatched'][idx] = isMatchInWindow(isMatch, entryInYearWindow);
      masks['3cMatched'][idx] = isMatchInWindow(isMatch, entryInBillingWindow3c);
      masks['6cMatched'][idx] = isMatchInWindow(isMatch, entryInBillingWindow6c);
      masks['12cMatched'][idx] = isMatchInWindow(isMatch, entryInBillingWindow12c);

      // Non-match
      masks.ytdUnmatched[idx] = isMatchInWindow(notMatch, entryThisYear);
      masks.mtdUnmatched[idx] = isMatchInWindow(notMatch, entryThisMonth);
      masks.btdUnmatched[idx] = isMatchInWindow(notMatch, entryThisBillingCycle);
      masks.tdyUnmatched[idx] = isMatchInWindow(notMatch, entryToday);
      masks['7dUnmatched'][idx] = isMatchInWindow(notMatch, entryInWeekWindow);
      masks['30dUnmatched'][idx] = isMatchInWindow(notMatch, entryInMonthWindow);
      masks['1yUnmatched'][idx] = isMatchInWindow(notMatch, entryInYearWindow);
      masks['3cUnmatched'][idx] = isMatchInWindow(notMatch, entryInBillingWindow3c);
      masks['6cUnmatched'][idx] = isMatchInWindow(notMatch, entryInBillingWindow6c);
      masks['12cUnmatched'][idx] = isMatchInWindow(notMatch, entryInBillingWindow12c);

      // Errors
      masks.ytdErrors[idx] = isMatchInWindow(isError, entryThisYear);
      masks.mtdErrors[idx] = isMatchInWindow(isError, entryThisMonth);
      masks.btdErrors[idx] = isMatchInWindow(isError, entryThisBillingCycle);
      masks.tdyErrors[idx] = isMatchInWindow(isError, entryToday);
      masks['7dErrors'][idx] = isMatchInWindow(isError, entryInWeekWindow);
      masks['30dErrors'][idx] = isMatchInWindow(isError, entryInMonthWindow);
      masks['1yErrors'][idx] = isMatchInWindow(isError, entryInYearWindow);
      masks['3cErrors'][idx] = isMatchInWindow(isError, entryInBillingWindow3c);
      masks['6cErrors'][idx] = isMatchInWindow(isError, entryInBillingWindow6c);
      masks['12cErrors'][idx] = isMatchInWindow(isError, entryInBillingWindow12c);

      // Total
      masks.ytdTotal[idx] = entryThisYear;
      masks.mtdTotal[idx] = entryThisMonth;
      masks.btdTotal[idx] = entryThisBillingCycle;
      masks.tdyTotal[idx] = entryToday;
      masks['7dTotal'][idx] = entryInWeekWindow;
      masks['30dTotal'][idx] = entryInMonthWindow;
      masks['1yTotal'][idx] = entryInYearWindow;
      masks['3cTotal'][idx] = entryInBillingWindow3c;
      masks['6cTotal'][idx] = entryInBillingWindow6c;
      masks['12cTotal'][idx] = entryInBillingWindow12c;
    });
    yield put({
      type: RT_API_USAGE.FETCH_USAGE_ENTRIES_SUCCEEDED,
      payload: {
        usageEntries: entries,
        usageMasks: masks,
      },
    });
    yield put({ type: RT_API_USAGE.GENERATE_USAGE_STATS });
  } catch (e) {
    yield put({
      type: RT_API_USAGE.FETCH_USAGE_ENTRIES_FAILED,
      payload: e.message,
    });
  } finally {
    yield delay(500);
    yield put(actions.unset('fetchUsageEntriesError'));
  }
}

/**
 * generateUsageStats - flow control for generating stat counts
 * @returns {Generator<*, void, *>}
 */
function* generateUsageStats() {
  try {
    /** @type {import('src/store/typings.js').TrackingEntry[]} */
    const entries = yield select(selectEntries);
    const { ytdMatched, mtdMatched, tdyMatched, ytdTotal, mtdTotal, tdyTotal } = yield select(selectMasks);
    const counts = {
      ytdTotalCount: 0,
      mtdTotalCount: 0,
      tdyTotalCount: 0,
      ytdMatchedCount: 0,
      mtdMatchedCount: 0,
      tdyMatchedCount: 0,
    };
    entries.forEach((entry, idx) => {
      counts.ytdTotalCount += ytdTotal[idx] ? entry.count : 0;
      counts.mtdTotalCount += mtdTotal[idx] ? entry.count : 0;
      counts.tdyTotalCount += tdyTotal[idx] ? entry.count : 0;
      counts.ytdMatchedCount += ytdMatched[idx] && !entry.cached ? entry.count : 0;
      counts.mtdMatchedCount += mtdMatched[idx] && !entry.cached ? entry.count : 0;
      counts.tdyMatchedCount += tdyMatched[idx] && !entry.cached ? entry.count : 0;
    });
    yield put({
      type: RT_API_USAGE.GENERATE_USAGE_STATS_SUCCEEDED,
      payload: counts,
    });
  } catch (err) {
    yield put({
      type: RT_API_USAGE.GENERATE_USAGE_STATS_FAILED,
      payload: err.message,
    });
  } finally {
    yield delay(500);
    yield put(actions.unset('generateStatsError'));
  }
}

function* generateUsageStatsSaga() {
  yield takeLatest(RT_API_USAGE.GENERATE_USAGE_STATS, generateUsageStats);
}

function* fetchUsageEntriesSaga() {
  yield takeLatest(RT_API_USAGE.FETCH_USAGE_ENTRIES, fetchUsageEntries);
}

function* fetchRttEntriesSaga() {
  yield takeLatest(RT_API_USAGE.FETCH_RTT_ENTRIES, fetchRttEntries);
}

function* updateFilterSaga() {
  yield takeLatest(RT_API_USAGE.UPDATE_FILTER, updateFilter);
}

function* fetchConfigSaga() {
  yield takeLatest(RT_API_USAGE.FETCH_CONFIG, fetchConfig);
}

function* fetchConfigListSaga() {
  yield takeLatest(RT_API_USAGE.FETCH_CONFIG_LIST, fetchConfigList);
}

export default [
  updateFilterSaga(),
  fetchRttEntriesSaga(),
  fetchUsageEntriesSaga(),
  generateUsageStatsSaga(),
  fetchConfigSaga(),
  fetchConfigListSaga(),
];
